import { supabase } from '@/integrations/supabase/client';
import { calculateHaversineDistance, estimateRoadDistance, DistanceRange } from '@/utils/cepUtils';
import { areaCoversPoint } from '@/utils/h3Cells';
import { fetchCoverageRowsForContext, type FreightContext } from '@/utils/deliveryContext';
import { geocodeCep } from '@/services/geocodingService';
import { fetchTollQuote } from '@/utils/tollService';

export interface SupplierFreightResult {
  supplierId: string;
  supplierName?: string;
  regionName?: string | null;
  freight: number;
  /** Pedágio estimado já somado ao `freight`. */
  toll?: number;
  matchType: 'cells' | 'circle' | 'cep' | 'none';
}

export interface FreightCalculationResult {
  totalFreight: number;
  /** Soma dos pedágios embutidos no frete total. */
  totalToll: number;
  perSupplier: SupplierFreightResult[];
  cepGeocoded: boolean;
}

interface TollAndDistance {
  amount: number;
  distanceKm: number | null;
}

/**
 * Consulta o pedágio estimado no servidor (Google Routes API + cache).
 * Falhas são silenciosas: o frete segue sem o adicional.
 */
async function fetchTollAndDistance(
  supplierId: string,
  coords: { lat: number; lng: number },
  context: FreightContext,
): Promise<TollAndDistance> {
  const quote = await fetchTollQuote(supplierId, coords, context);
  return {
    amount: quote.amount,
    distanceKm: quote.distanceMeters != null ? quote.distanceMeters / 1000 : null,
  };
}

const normalizeCep = (cep: string): string => (cep || '').replace(/\D/g, '');

/**
 * Coordenadas do endereço cadastrado do fornecedor (não confundir com a
 * origem que ele pode ter escolhido ao desenhar uma região específica: a
 * distância cobrada no frete parte sempre do endereço real, mesmo quando a
 * área de cobertura foi desenhada a partir de outro ponto de partida).
 */
async function fetchSupplierOriginCoords(supplierId: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const { data } = await supabase
      .from('supplier_address')
      .select('address_code')
      .eq('supplier_id', supplierId)
      .maybeSingle();
    if (!data?.address_code) return null;
    const geo = await geocodeCep(data.address_code);
    return geo ? { lat: geo.lat, lng: geo.lng } : null;
  } catch (error) {
    console.error('[calculateDeliveryFee] Erro ao buscar endereço do fornecedor:', error);
    return null;
  }
}

/**
 * Encontra a faixa de distância que cobre a distância fornecida (tratando decimais sem lacunas)
 */
function findMatchingDistanceRange(distanceKm: number, ranges: DistanceRange[]): DistanceRange | null {
  if (!ranges || ranges.length === 0) return null;

  const sorted = [...ranges].sort((a, b) => a.minKm - b.minKm);

  // Tenta encontrar a faixa exata
  for (const range of sorted) {
    if (distanceKm >= range.minKm && distanceKm <= range.maxKm) {
      return range;
    }
  }

  // Se não encontrar exato, tenta arredondar para baixo
  const floorDistance = Math.floor(distanceKm);
  if (floorDistance !== distanceKm) {
    for (const range of sorted) {
      if (floorDistance >= range.minKm && floorDistance <= range.maxKm) {
        return range;
      }
    }
  }

  return null;
}

/**
 * Retorna a distância a ser usada para precificação.
 * Prioriza a distância real da rota (Google Routes, vinda junto com o pedágio).
 * Se não estiver disponível, usa Haversine × fator de estrada.
 */
function resolvePricingDistanceKm(
  eventCoords: { lat: number; lng: number },
  supplierCenter: { lat: number; lng: number } | null,
  routeDistanceKm: number | null,
): number {
  if (routeDistanceKm != null && routeDistanceKm > 0) {
    return routeDistanceKm;
  }

  if (supplierCenter) {
    const straight = calculateHaversineDistance(
      [eventCoords.lat, eventCoords.lng],
      [supplierCenter.lat, supplierCenter.lng],
    );
    return estimateRoadDistance(straight) / 1000;
  }

  return 0;
}

/**
 * Centralized delivery-fee calculator.
 *
 * Looks up `supplier_cep_coverage` for each supplier and matches against:
 *   1. Circle areas (Haversine vs lat/lng + radius_meters)
 *   2. CEP ranges (cep_start / cep_end)
 *
 * If no coverage matches, returns 0 freight for that supplier (no fallback).
 */
export const calculateFreightForEvent = async (
  cep: string,
  supplierIds: string[],
  context: FreightContext = 'event',
): Promise<FreightCalculationResult> => {
  const normalizedCep = normalizeCep(cep);
  const uniqueSupplierIds = Array.from(new Set(supplierIds.filter(Boolean)));

  if (normalizedCep.length !== 8 || uniqueSupplierIds.length === 0) {
    return { totalFreight: 0, totalToll: 0, perSupplier: [], cepGeocoded: false };
  }

  // Geocode once for all circle matches
  const eventCoords = await geocodeCep(normalizedCep);
  const cepNum = parseInt(normalizedCep, 10);
  const perSupplier: SupplierFreightResult[] = [];

  for (const supplierId of uniqueSupplierIds) {
    const ranges = await fetchCoverageRowsForContext(supplierId, context, { onlyEnabled: true });

    if (!ranges || ranges.length === 0) {
      perSupplier.push({ supplierId, freight: 0, matchType: 'none' });
      continue;
    }

    // A distância cobrada parte sempre do endereço cadastrado do fornecedor,
    // mesmo que a região de cobertura (círculo ou área pintada) tenha sido
    // desenhada a partir de um ponto de partida diferente.
    const supplierOrigin = await fetchSupplierOriginCoords(supplierId);

    let matched: any = null;
    let matchType: 'cells' | 'circle' | 'cep' | 'none' = 'none';
    let finalFreight = 0;
    let supplierCenter: { lat: number; lng: number } | null = null;

    // 0. Área desenhada (células H3) — tem prioridade sobre círculo/CEP
    if (eventCoords) {
      for (const r of ranges as any[]) {
        if (areaCoversPoint(r.celulas, eventCoords.lat, eventCoords.lng)) {
          matched = r;
          matchType = 'cells';
          const pricingOrigin = supplierOrigin
            || (r.latitude != null && r.longitude != null ? { lat: Number(r.latitude), lng: Number(r.longitude) } : null);
          if (r.free_delivery) {
            finalFreight = 0;
          } else if (r.per_km_enabled && pricingOrigin && eventCoords) {
            const straight = calculateHaversineDistance(
              [eventCoords.lat, eventCoords.lng],
              [pricingOrigin.lat, pricingOrigin.lng],
            );
            const km = estimateRoadDistance(straight) / 1000;
            const raw = (Number(r.per_km_base_fee) || 0) + (Number(r.per_km_rate) || 0) * km;
            finalFreight = Math.round(Math.max(raw, Number(r.per_km_min_fee) || 0) * 100) / 100;
          } else {
            finalFreight = Number(r.delivery_fee) || 0;
          }
          break;
        }
      }
    }

    for (const r of matchType === 'none' ? (ranges as any[]) : []) {
      // 1. Circle area
      if (r.area_type === 'circle' && r.latitude && r.longitude && r.radius_meters && eventCoords) {
        // A geometria do círculo (cobertura) usa sempre o centro salvo, mas o
        // preço por distância parte do endereço real do fornecedor.
        const straightDistance = calculateHaversineDistance(
          [eventCoords.lat, eventCoords.lng],
          [Number(r.latitude), Number(r.longitude)],
        );
        const pricingOrigin = supplierOrigin || { lat: Number(r.latitude), lng: Number(r.longitude) };
        const pricingStraightDistance = supplierOrigin
          ? calculateHaversineDistance([eventCoords.lat, eventCoords.lng], [pricingOrigin.lat, pricingOrigin.lng])
          : straightDistance;
        const roadDistanceKm = estimateRoadDistance(pricingStraightDistance) / 1000;

        if (estimateRoadDistance(straightDistance) <= Number(r.radius_meters)) {
          matched = r;
          matchType = 'circle';
          supplierCenter = pricingOrigin;

          // Apply distance-based ranges if enabled
          if (r.distance_ranges_enabled && Array.isArray(r.distance_ranges) && r.distance_ranges.length > 0) {
            const range = findMatchingDistanceRange(roadDistanceKm, r.distance_ranges);
            if (range) {
              finalFreight = range.freeDelivery ? 0 : Number(range.deliveryFee);
            } else {
              finalFreight = r.free_delivery ? 0 : Number(r.delivery_fee) || 0;
            }
          } else {
            finalFreight = r.free_delivery ? 0 : Number(r.delivery_fee) || 0;
          }
          break;
        }
      }
      // 2. CEP range
      if (r.cep_start && r.cep_end) {
        const start = parseInt(String(r.cep_start).replace(/\D/g, ''), 10);
        const end = parseInt(String(r.cep_end).replace(/\D/g, ''), 10);
        if (!isNaN(start) && !isNaN(end) && cepNum >= start && cepNum <= end) {
          matched = r;
          matchType = 'cep';
          finalFreight = r.free_delivery ? 0 : Number(r.delivery_fee) || 0;
          break;
        }
      }
    }

    // Pedágio (Google Routes API, server-side) somado ao frete cobrado.
    // Aproveitamos a mesma chamada para obter a distância real da rota
    // (o servidor parte sempre do endereço cadastrado do fornecedor).
    // Também buscamos a rota quando o preço é por distância mesmo que a
    // primeira estimativa (antes de reavaliar por faixas) tenha dado zero.
    const needsDistancePricing = !!(matched?.per_km_enabled || matched?.distance_ranges_enabled);
    let toll = 0;
    let routeDistanceKm: number | null = null;
    if (matchType !== 'none' && (finalFreight > 0 || needsDistancePricing) && eventCoords) {
      const quote = await fetchTollAndDistance(supplierId, eventCoords, context);
      toll = quote.amount;
      routeDistanceKm = quote.distanceKm;
      finalFreight = Math.round((finalFreight + toll) * 100) / 100;
    }

    // Para áreas desenhadas (cells) com faixas por distância, reavalia o frete
    // usando a distância real da rota quando disponível.
    if (matchType === 'cells' && matched && eventCoords) {
      const center = supplierOrigin || supplierCenter || (matched?.latitude != null && matched?.longitude != null
        ? { lat: Number(matched.latitude), lng: Number(matched.longitude) }
        : null);
      const pricingDistance = resolvePricingDistanceKm(eventCoords, center, routeDistanceKm);

      if (
        matched.distance_ranges_enabled &&
        Array.isArray(matched.distance_ranges) &&
        matched.distance_ranges.length > 0
      ) {
        const range = findMatchingDistanceRange(pricingDistance, matched.distance_ranges);
        if (range) {
          const rangeFee = range.freeDelivery ? 0 : Number(range.deliveryFee) || 0;
          // Pedágio só entra quando a faixa cobra frete — faixa gratuita
          // (frete grátis) não pode virar paga por causa de pedágio.
          finalFreight = Math.round((rangeFee + (rangeFee > 0 ? toll : 0)) * 100) / 100;
        }
      }
    }

    perSupplier.push({
      supplierId,
      regionName: matched?.region_name ?? null,
      freight: finalFreight,
      toll,
      matchType,
    });
  }

  const totalFreight = perSupplier.reduce((sum, s) => sum + s.freight, 0);
  const totalToll = perSupplier.reduce((sum, s) => sum + (s.toll || 0), 0);

  return {
    totalFreight: Math.round(totalFreight * 100) / 100,
    totalToll: Math.round(totalToll * 100) / 100,
    perSupplier,
    cepGeocoded: !!eventCoords,
  };
};

/**
 * Extract supplier IDs from an event's menu/delivery_supplier/supplier_id fields.
 */
export const extractSupplierIdsFromEvent = (event: {
  supplier_id?: string | null;
  menu?: any;
  delivery_supplier?: any;
  selected_suppliers?: any;
}): string[] => {
  const ids = new Set<string>();

  if (event.supplier_id) ids.add(event.supplier_id);

  // delivery_supplier may be an array of objects with supplier_id
  if (Array.isArray(event.delivery_supplier)) {
    for (const ds of event.delivery_supplier) {
      if (ds?.supplier_id) ids.add(ds.supplier_id);
    }
  }

  // selected_suppliers may be an object keyed by supplier_id or an array
  if (event.selected_suppliers) {
    if (Array.isArray(event.selected_suppliers)) {
      for (const ss of event.selected_suppliers) {
        if (typeof ss === 'string') ids.add(ss);
        else if (ss?.supplier_id) ids.add(ss.supplier_id);
        else if (ss?.id) ids.add(ss.id);
      }
    } else if (typeof event.selected_suppliers === 'object') {
      Object.keys(event.selected_suppliers).forEach((k) => {
        // only add UUID-shaped keys
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(k)) {
          ids.add(k);
        }
      });
    }
  }

  return Array.from(ids);
};
